import type { ApiFieldIssue } from '@/services/api/ApiFieldIssue'
import type { ProblemFieldIssues } from '@/services/forms/ProblemFieldIssues'
import type { FormFieldMap } from '@/services/forms/FormFieldMap'
import type { FieldServerMessage } from '@/types/FieldServerMessage'

const fieldPathPattern =
  /^(?:[A-Za-z_$][A-Za-z0-9_$]*|\[(?:[A-Za-z_$][A-Za-z0-9_$]*|\d+)\])(?:\.(?:[A-Za-z_$][A-Za-z0-9_$]*|\d+)|\[(?:[A-Za-z_$][A-Za-z0-9_$]*|\d+)\])*$/u

const assertFieldMap = <UiPath extends string>(fieldMap: FormFieldMap<UiPath>): void => {
  const wires = new Set<string>()

  for (const field of fieldMap) {
    if (!fieldPathPattern.test(field.ui) || !fieldPathPattern.test(field.wire)) {
      throw new TypeError(`Invalid form field mapping: ${field.ui} -> ${field.wire}`)
    }
    if (wires.has(field.wire)) {
      throw new TypeError(`Duplicate wire field mapping: ${field.wire}`)
    }
    wires.add(field.wire)
  }
}

/**
 * Resolves the display text for one machine-readable ApiFieldIssue. The
 * caller supplies this (see usePresentationCodes.resolveFieldIssueMessage)
 * so this services-layer module stays decoupled from the i18n composable.
 */
type FieldIssueResolver = (issue: ApiFieldIssue) => string

/**
 * A callback, not the whole form object: TanStack Form's setFieldMeta is
 * generic only over the field name, not the meta shape (fixed internally to
 * its own AnyFieldLikeMetaBase), so no interface here can structurally
 * describe it without fighting TypeScript's generic variance rules. The
 * caller already has full type information at its own setFieldMeta call
 * site -- let it build this narrow closure there instead, e.g.:
 *   (field, messages) => form.setFieldMeta(field, (meta) => ({
 *     ...meta,
 *     errorMap: { ...meta.errorMap, onServer: messages }
 *   }))
 * This also keeps the adapter itself validation-library-agnostic.
 */
type SetFieldServerErrors<UiPath extends string> = (
  field: UiPath,
  messages: FieldServerMessage[]
) => void

type ApplyProblemFieldErrorsResult = Readonly<{ unmapped: readonly ApiFieldIssue[] }>

/** Finds the mapped field a dotted wire path belongs to, walking up to the nearest mapped ancestor. */
const collapseToMappedField = <UiPath extends string>(
  path: string,
  wireToField: ReadonlyMap<string, FormFieldMap<UiPath>[number]>
): Readonly<{ field: FormFieldMap<UiPath>[number]; exact: boolean }> | null => {
  let candidate = path
  for (;;) {
    const field = wireToField.get(candidate)
    if (field !== undefined) return { exact: candidate === path, field }

    const cut = candidate.lastIndexOf('.')
    if (cut === -1) return null
    candidate = candidate.slice(0, cut)
  }
}

const applyProblemFieldErrors = <UiPath extends string>(
  setFieldServerErrors: SetFieldServerErrors<UiPath>,
  problem: ProblemFieldIssues,
  fieldMap: FormFieldMap<UiPath>,
  resolveFieldIssue: FieldIssueResolver
): ApplyProblemFieldErrorsResult => {
  assertFieldMap(fieldMap)

  const wireToField = new Map(fieldMap.map((field) => [field.wire, field] as const))
  const messagesByUi = new Map<UiPath, FieldServerMessage[]>()
  const unmapped: ApiFieldIssue[] = []

  for (const [path, issues] of Object.entries(problem.fieldIssues)) {
    const target = collapseToMappedField(path, wireToField)
    if (target === null) {
      unmapped.push(...issues)
      continue
    }

    const rowLabel = target.field.rowLabel
    const messages: FieldServerMessage[] = issues.map((issue) =>
      !target.exact && rowLabel !== undefined
        ? () => `${rowLabel(path)}: ${resolveFieldIssue(issue)}`
        : () => resolveFieldIssue(issue)
    )

    const existing = messagesByUi.get(target.field.ui) ?? []
    existing.push(...messages)
    messagesByUi.set(target.field.ui, existing)
  }

  const dispatchedUi = new Set<UiPath>()
  for (const field of fieldMap) {
    if (dispatchedUi.has(field.ui)) continue
    dispatchedUi.add(field.ui)

    const messages = messagesByUi.get(field.ui)
    if (messages !== undefined && messages.length > 0) {
      setFieldServerErrors(field.ui, messages)
    }
  }

  return { unmapped }
}

/** Empties every mapped field's server slot, so a later attempt or replacement response never keeps an earlier rejection's message. */
const clearProblemFieldErrors = <UiPath extends string>(
  setFieldServerErrors: SetFieldServerErrors<UiPath>,
  fieldMap: FormFieldMap<UiPath>
): void => {
  for (const field of new Set(fieldMap.map((entry) => entry.ui))) {
    setFieldServerErrors(field, [])
  }
}

export const formValidation = Object.freeze({ applyProblemFieldErrors, clearProblemFieldErrors })
